/**
 * A JSON-Schema-driven form: given a (possibly $ref'd) schema and a value, renders
 * inputs for it and reports changes back. This is what turns "the contract has a
 * request body" into fields a developer can fill in, without a hand-written form per
 * endpoint — the same reason `contract.ts` reads the spec live rather than a fixed list.
 *
 * Only ever pointed at REQUEST shapes (parameters, request bodies) — response schemas
 * are never run through this, they are only ever rendered as the literal JSON that came
 * back (see `json-view.tsx`). That is deliberate: a couple of response schemas carry
 * prose that names a vendor (redacted in `contract.ts`'s `sanitize`), and simply never
 * turning a response schema into on-screen field labels/descriptions is the cheapest way
 * to make sure that text has no path to the screen at all.
 *
 * `$ref` and `allOf` are resolved as the form descends (`resolveSchema`); `oneOf`/`anyOf`
 * that only ever constrain which properties are required (the pattern this contract
 * uses — see EnvelopeCreate's "template_id OR document_file_ids") become an advisory
 * note rather than a branching form. A depth guard and a Form/Raw-JSON toggle at the
 * root are the escape hatches for anything shaped more strangely than that.
 */
import { FC, useEffect, useMemo, useState } from 'react';

import { Switch } from 'views/design-system/components';

import { OpenAPIDoc, resolveRef, sanitize } from './contract';

const MAX_DEPTH = 6;

export function resolveSchema(doc: OpenAPIDoc, schema: any): any {
	if (!schema || typeof schema !== 'object') return {};
	let s = schema;
	let guard = 0;
	while (s && typeof s.$ref === 'string' && guard < 12) {
		s = resolveRef(doc, s.$ref) || {};
		guard += 1;
	}
	if (Array.isArray(s.allOf)) {
		const merged: any = { type: 'object', properties: {}, required: [] as string[] };
		for (const sub of s.allOf) {
			const r = resolveSchema(doc, sub);
			if (r.properties) Object.assign(merged.properties, r.properties);
			if (Array.isArray(r.required)) merged.required.push(...r.required);
			for (const k of [
				'description',
				'enum',
				'const',
				'format',
				'items',
				'additionalProperties',
				'default',
				'type',
			]) {
				if (merged[k] === undefined && r[k] !== undefined) merged[k] = r[k];
			}
		}
		merged.required = Array.from(new Set(merged.required));
		if (s.oneOf) merged.oneOf = s.oneOf;
		if (s.anyOf) merged.anyOf = s.anyOf;
		if (s.description) merged.description = merged.description || s.description;
		return merged;
	}
	return s;
}

/** oneOf/anyOf whose branches carry only a `required` list is a "one of these must be
 *  present" constraint, not a real type split — the only shape this contract's request
 *  bodies use it for. Anything else (a branch with its own `properties`/`type`) is left
 *  alone; the field falls through to the object/array/string handling below same as if
 *  there were no oneOf at all, which is a reasonable enough default and never crashes. */
function requiredNote(resolved: any): string | null {
	const branches = resolved.oneOf || resolved.anyOf;
	if (!Array.isArray(branches) || !branches.length) return null;
	const onlyRequired = branches.every(
		(b: any) => b && !b.$ref && Array.isArray(b.required) && Object.keys(b).every(k => k === 'required')
	);
	if (!onlyRequired) return null;
	const label = resolved.oneOf ? 'Exactly one of' : 'At least one of';
	return `${label}: ${branches.map((b: any) => b.required.join(' + ')).join(', ')}`;
}

export function defaultFor(resolved: any): any {
	if (resolved.default !== undefined) return resolved.default;
	if (resolved.const !== undefined) return resolved.const;
	switch (resolved.type) {
		case 'boolean':
			return false;
		case 'array':
			return [];
		case 'object':
			return {};
		default:
			return '';
	}
}

const humanize = (name?: string) =>
	name ? name.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()) : '';

/** The generic fallback: anything the form can't shape (past the depth guard, or a real
 *  branching union) is still editable, just as text. Nothing is ever hidden outright. */
const RawJsonField: FC<{ value: any; onChange: (v: any) => void; label?: string }> = ({
	value,
	onChange,
	label,
}) => {
	const [text, setText] = useState(() => JSON.stringify(value ?? null, null, 2));
	const [err, setErr] = useState<string | null>(null);
	return (
		<div className="dfield">
			{label ? <div className="dflabel">{label}</div> : null}
			<textarea
				className="mono raw-json"
				rows={4}
				value={text}
				onChange={e => {
					setText(e.target.value);
					try {
						onChange(JSON.parse(e.target.value));
						setErr(null);
					} catch {
						setErr('Not valid JSON yet — not sent until this parses.');
					}
				}}
			/>
			{err ? <div className="dferr">{err}</div> : null}
		</div>
	);
};

interface FieldProps {
	doc: OpenAPIDoc;
	schema: any;
	value: any;
	onChange: (v: any) => void;
	name?: string;
	required?: boolean;
	depth: number;
}

/** A `additionalProperties`-shaped map with no fixed `properties`: a free key/value list
 *  (`Metadata`, `prefill.kyc`, …). Keys are edited as plain text so two in-flight edits
 *  never collide, which a value keyed straight off the object would risk mid-keystroke.
 *
 *  A `function` declaration rather than the `const FC =` used elsewhere in this file:
 *  this and `SchemaField` below call each other (a map's values can themselves need the
 *  full field renderer), and a plain function declaration is hoisted, which is what lets
 *  either one reference the other regardless of which is defined first in the file. */
function MapField({ doc, schema, value, onChange, name }: FieldProps) {
	const resolved = useMemo(() => resolveSchema(doc, schema), [doc, schema]);
	const valSchema =
		resolved.additionalProperties && typeof resolved.additionalProperties === 'object'
			? resolved.additionalProperties
			: null;
	const obj = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
	const [pendingKey, setPendingKey] = useState('');

	return (
		<div className="dfield map-field">
			<div className="dflabel">{humanize(name)}</div>
			{sanitize(resolved.description) ? (
				<div className="dfhint">{sanitize(resolved.description)}</div>
			) : null}
			{Object.entries(obj).map(([k, v]) => (
				<div className="map-row" key={k}>
					<input
						className="mono map-key"
						value={k}
						onChange={e => {
							const next: Record<string, any> = { ...obj };
							delete next[k];
							next[e.target.value] = v;
							onChange(next);
						}}
					/>
					{valSchema ? (
						// Genuine mutual recursion with `SchemaField` below (a map's values can
						// themselves be full schema-shaped fields) — hoisted `function`
						// declarations make this safe at runtime; the lint rule still flags
						// whichever one is textually second no matter which way round they go.
						// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
						<SchemaField
							doc={doc}
							schema={valSchema}
							value={v}
							onChange={nv => onChange({ ...obj, [k]: nv })}
							depth={2}
						/>
					) : (
						<input
							className="map-val"
							value={typeof v === 'string' ? v : JSON.stringify(v)}
							onChange={e => onChange({ ...obj, [k]: e.target.value })}
						/>
					)}
					<button
						type="button"
						className="btn danger"
						onClick={() => {
							const next: Record<string, any> = { ...obj };
							delete next[k];
							onChange(next);
						}}
					>
						Remove
					</button>
				</div>
			))}
			<div className="map-add">
				<input
					placeholder="key"
					value={pendingKey}
					onChange={e => setPendingKey(e.target.value)}
				/>
				<button
					type="button"
					className="btn"
					disabled={!pendingKey || pendingKey in obj}
					onClick={() => {
						onChange({ ...obj, [pendingKey]: valSchema ? defaultFor(resolveSchema(doc, valSchema)) : '' });
						setPendingKey('');
					}}
				>
					+ Add key
				</button>
			</div>
		</div>
	);
}

export function SchemaField({ doc, schema, value, onChange, name, required, depth }: FieldProps) {
	const resolved = useMemo(() => resolveSchema(doc, schema), [doc, schema]);

	// Hooks run unconditionally, before any of the type-specific returns below.
	useEffect(() => {
		if (resolved.const !== undefined && value !== resolved.const) onChange(resolved.const);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resolved.const]);

	if (depth > MAX_DEPTH) {
		return <RawJsonField value={value} onChange={onChange} label={humanize(name)} />;
	}

	const description = sanitize(resolved.description);

	if (resolved.const !== undefined) {
		return (
			<div className="dfield">
				<div className="dflabel">{humanize(name)}</div>
				<div className="const-val mono">{String(resolved.const)}</div>
				{description ? <div className="dfhint">{description}</div> : null}
			</div>
		);
	}

	if (Array.isArray(resolved.enum)) {
		return (
			<div className="dfield">
				<div className="dflabel">
					{humanize(name)}
					{required ? <span className="req">*</span> : null}
				</div>
				<select value={value ?? ''} onChange={e => onChange(e.target.value || undefined)}>
					<option value="">{required ? '— choose —' : '(omit)'}</option>
					{resolved.enum.map((v: string) => (
						<option key={v} value={v}>
							{v}
						</option>
					))}
				</select>
				{description ? <div className="dfhint">{description}</div> : null}
			</div>
		);
	}

	if (resolved.type === 'boolean') {
		return (
			<div className="dfield dfield--switch">
				<Switch checked={!!value} onChange={onChange} label={humanize(name)} />
				<span className="dflabel inline">{humanize(name)}</span>
				{description ? <div className="dfhint">{description}</div> : null}
			</div>
		);
	}

	if (resolved.type === 'integer' || resolved.type === 'number') {
		return (
			<div className="dfield">
				<div className="dflabel">
					{humanize(name)}
					{required ? <span className="req">*</span> : null}
				</div>
				<input
					type="number"
					step={resolved.type === 'integer' ? 1 : 'any'}
					min={resolved.minimum}
					max={resolved.maximum}
					value={value ?? ''}
					onChange={e => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
				/>
				{description ? <div className="dfhint">{description}</div> : null}
			</div>
		);
	}

	if (resolved.type === 'array') {
		const arr: any[] = Array.isArray(value) ? value : [];
		const itemSchema = resolved.items || {};
		return (
			<div className="dfield arr-field">
				<div className="dflabel">
					{humanize(name)}
					{required ? <span className="req">*</span> : null}
					<button
						type="button"
						className="btn arr-add"
						onClick={() => onChange([...arr, defaultFor(resolveSchema(doc, itemSchema))])}
					>
						+ Add
					</button>
				</div>
				{arr.length === 0 ? <div className="dfhint">Empty list</div> : null}
				{arr.map((item, i) => (
					// eslint-disable-next-line react/no-array-index-key
					<div className="arr-row" key={i}>
						<div className="arr-item">
							<SchemaField
								doc={doc}
								schema={itemSchema}
								value={item}
								onChange={v => {
									const next = [...arr];
									next[i] = v;
									onChange(next);
								}}
								depth={depth + 1}
							/>
						</div>
						<button
							type="button"
							className="btn danger arr-remove"
							onClick={() => onChange(arr.filter((_, j) => j !== i))}
						>
							Remove
						</button>
					</div>
				))}
				{description ? <div className="dfhint">{description}</div> : null}
			</div>
		);
	}

	if (
		resolved.additionalProperties &&
		resolved.additionalProperties !== false &&
		!resolved.properties
	) {
		return <MapField doc={doc} schema={schema} value={value} onChange={onChange} name={name} depth={depth} />;
	}

	if (resolved.type === 'object' || resolved.properties) {
		const note = requiredNote(resolved);
		const obj = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
		const props = resolved.properties || {};
		const reqSet = new Set<string>(resolved.required || []);
		const body = (
			<>
				{Object.keys(props).map(key => (
					<SchemaField
						key={key}
						doc={doc}
						schema={props[key]}
						value={obj[key]}
						onChange={v => onChange({ ...obj, [key]: v })}
						name={key}
						required={reqSet.has(key)}
						depth={depth + 1}
					/>
				))}
				{note ? <div className="dfhint obj-note">{note}</div> : null}
				{Object.keys(props).length === 0 ? (
					<div className="dfhint">No fixed fields — nothing required here.</div>
				) : null}
			</>
		);
		if (depth === 0) return <>{body}</>;
		return (
			<div className="dfield obj-field">
				<div className="dflabel">
					{humanize(name)}
					{required ? <span className="req">*</span> : null}
				</div>
				{description ? <div className="dfhint">{description}</div> : null}
				<div className="obj-nest">{body}</div>
			</div>
		);
	}

	const inputType =
		resolved.format === 'date'
			? 'date'
			: resolved.format === 'date-time'
				? 'datetime-local'
				: resolved.format === 'email'
					? 'email'
					: resolved.format === 'uri'
						? 'url'
						: 'text';

	return (
		<div className="dfield">
			<div className="dflabel">
				{humanize(name)}
				{required ? <span className="req">*</span> : null}
				{resolved.maxLength ? (
					<span className="fcount">
						{(value || '').length}/{resolved.maxLength}
					</span>
				) : null}
			</div>
			<input
				type={inputType}
				maxLength={resolved.maxLength}
				placeholder={resolved.example !== undefined ? String(resolved.example) : undefined}
				value={value ?? ''}
				onChange={e => onChange(e.target.value)}
			/>
			{description ? <div className="dfhint">{description}</div> : null}
		</div>
	);
}

/**
 * The request-body root: a Form view (built from `SchemaField`) with a Raw JSON escape
 * hatch. Anything that fails to round-trip is kept in Raw with an inline error rather
 * than silently discarded — "not sent until fixed" beats guessing what the user meant.
 */
export const BodyEditor: FC<{
	doc: OpenAPIDoc;
	schema: any;
	value: any;
	onChange: (v: any) => void;
}> = ({ doc, schema, value, onChange }) => {
	const [mode, setMode] = useState<'form' | 'raw'>('form');
	const [rawText, setRawText] = useState(() => JSON.stringify(value ?? {}, null, 2));
	const [rawErr, setRawErr] = useState<string | null>(null);

	return (
		<div className="body-editor">
			<div className="mode-toggle">
				<button
					type="button"
					className={`pre${mode === 'form' ? ' on' : ''}`}
					onClick={() => setMode('form')}
				>
					Form
				</button>
				<button
					type="button"
					className={`pre${mode === 'raw' ? ' on' : ''}`}
					onClick={() => {
						setRawText(JSON.stringify(value ?? {}, null, 2));
						setRawErr(null);
						setMode('raw');
					}}
				>
					Raw JSON
				</button>
			</div>
			{mode === 'form' ? (
				<div className="body-form">
					<SchemaField doc={doc} schema={schema} value={value} onChange={onChange} depth={0} />
				</div>
			) : (
				<div className="body-raw">
					<textarea
						rows={16}
						className="mono raw-json"
						value={rawText}
						onChange={e => {
							setRawText(e.target.value);
							try {
								onChange(JSON.parse(e.target.value));
								setRawErr(null);
							} catch {
								setRawErr('Invalid JSON — not sent until this parses.');
							}
						}}
					/>
					{rawErr ? <div className="dferr">{rawErr}</div> : null}
				</div>
			)}
		</div>
	);
};
